import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Reset link is missing a token");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Reset failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#0A0A0A] text-[#F3F4F6] p-6"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-sm space-y-7">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
            <img src="/icons/logo-nl.png" alt="" className="h-7 w-7 object-contain" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">NoteLosh</span>
        </div>

        {done ? (
          <div className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-6 text-center space-y-3">
            <div className="mx-auto h-11 w-11 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/40 flex items-center justify-center text-[#3B82F6]">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h2 className="font-heading text-lg font-semibold">Password updated</h2>
            <p className="text-sm text-[#9CA3AF]">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Choose a new password</h2>
              <p className="text-sm text-[#9CA3AF]">
                Enter a new password for your account. This link expires in 2 hours.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs text-[#9CA3AF]">New password</Label>
                <Input
                  id="password"
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm" className="text-xs text-[#9CA3AF]">Confirm password</Label>
                <Input
                  id="confirm"
                  data-testid="reset-password-confirm-input"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !token}
                data-testid="reset-password-submit-btn"
                className="w-full rounded-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium transition-all glow-blue"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…</> : "Update password"}
              </Button>
            </form>

            <Link to="/login" className="block text-center text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors">
              ← Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
